import { HomeyAPI } from 'homey-api';

import { RouteManager } from '../../../RouteManager';
import { ToastManager } from '../../../ToastManager';
import type { CommandItem, CommandProviderArgs } from '../command-provider.types';
import { COMMAND_SECTIONS } from '../command-types.ts';

import type { AdvancedFlowsStoreState } from '../../../store/advanced-flow/AdvancedFlowsStore.ts';
import { AdvancedFlowUtils } from '../../../store/advanced-flow/AdvancedFlowUtils';
import type { DeviceProps } from '../../../store/devices/DevicesStore.ts';
import { DeviceUtils } from '../../../store/devices/DeviceUtils.js';
import type { FlowsStoreState } from '../../../store/flow/FlowsStore.ts';
import { FlowUtils } from '../../../store/flow/FlowUtils';
import { userMeDraftStore } from '../../../store/users/UserMeDraftStore.ts';
import type { UserProps } from '../../../store/users/UsersStore';

import { glyph } from '../../../theme/glyph';
import { theme } from '../../../theme/theme';

import { getNumberDeviceLogIds, getNumberDeviceLogsCount } from './getNumberDeviceLogsCount.ts';
import { asStringArray, copyToClipboard, isMatch, normalize, toastAdd } from './provider-utils.ts';

const SYSTEM_QUICK_ACTION_CAPABILITIES = ['onoff', 'speaker_playing', 'button'];
const DEVICE_OWNER_URI_PREFIX = 'homey:device:';
const EMPTY_BY_ID = {};
const deviceFlowCountsCache = new WeakMap<object, WeakMap<object, Map<string, number>>>();

type DeviceProviderApi = CommandProviderArgs['api'];
type DeviceProviderI18n = CommandProviderArgs['i18n'];

function getFavoriteDevices(userMe: UserProps): string[] {
  return asStringArray(userMe.properties?.favoriteDevices);
}

function getAppIdFromDriverId(driverId: unknown): string | null {
  if (typeof driverId !== 'string') {
    return null;
  }

  const match = /^homey:app:([^:]+)/.exec(driverId);

  return match?.[1] ?? null;
}

function getDeviceFlowsCount({
  deviceId,
  flowsById,
  advancedFlowsById,
}: {
  deviceId: string | undefined;
  flowsById: FlowsStoreState['byId'];
  advancedFlowsById: AdvancedFlowsStoreState['byId'];
}): number | null {
  if (deviceId == null) {
    return null;
  }

  const countsByDeviceId = getDeviceFlowCountsById({
    flowsById,
    advancedFlowsById,
  });

  return countsByDeviceId.get(deviceId) ?? 0;
}

function getDeviceFlowCountsById({
  flowsById,
  advancedFlowsById,
}: {
  flowsById: FlowsStoreState['byId'];
  advancedFlowsById: AdvancedFlowsStoreState['byId'];
}): Map<string, number> {
  const flowsCacheKey = flowsById ?? EMPTY_BY_ID;
  let advancedFlowsCache = deviceFlowCountsCache.get(flowsCacheKey);

  if (advancedFlowsCache == null) {
    advancedFlowsCache = new WeakMap<object, Map<string, number>>();
    deviceFlowCountsCache.set(flowsCacheKey, advancedFlowsCache);
  }

  const advancedFlowsCacheKey = advancedFlowsById ?? EMPTY_BY_ID;
  const cachedCountsByDeviceId = advancedFlowsCache.get(advancedFlowsCacheKey);

  if (cachedCountsByDeviceId != null) {
    return cachedCountsByDeviceId;
  }

  const countsByDeviceId = new Map<string, number>();

  for (const flow of Object.values(flowsById ?? {})) {
    countDeviceOwnerUris(countsByDeviceId, FlowUtils.getOwnerUrisSetFromCards(flow));
  }

  for (const advancedFlow of Object.values(advancedFlowsById ?? {})) {
    countDeviceOwnerUris(
      countsByDeviceId,
      AdvancedFlowUtils.getOwnerUrisSetFromCards(advancedFlow),
    );
  }

  advancedFlowsCache.set(advancedFlowsCacheKey, countsByDeviceId);

  return countsByDeviceId;
}

function countDeviceOwnerUris(countsByDeviceId: Map<string, number>, ownerUris: Set<string>): void {
  for (const ownerUri of ownerUris) {
    if (ownerUri.startsWith(DEVICE_OWNER_URI_PREFIX) !== true) {
      continue;
    }

    const deviceId = ownerUri.slice(DEVICE_OWNER_URI_PREFIX.length);

    if (deviceId === '') {
      continue;
    }

    countsByDeviceId.set(deviceId, (countsByDeviceId.get(deviceId) ?? 0) + 1);
  }
}

async function toggleFavoriteDevice({
  device,
  userMe,
  api,
  i18n,
}: {
  device: DeviceProps;
  userMe: UserProps;
  api: DeviceProviderApi;
  i18n: DeviceProviderI18n;
}): Promise<void> {
  if (device == null || userMe == null || api == null) {
    return;
  }

  const favoriteName = `**${device.name}**`;
  const isFavoriteDevice = DeviceUtils.isFavoriteDevice(device, userMe);
  const currentFavoriteDevices = getFavoriteDevices(userMe);
  const nextFavoriteDevices =
    isFavoriteDevice === true
      ? currentFavoriteDevices.filter((id) => {
          return id !== device.id;
        })
      : currentFavoriteDevices.includes(device.id)
        ? currentFavoriteDevices
        : [...currentFavoriteDevices, device.id];

  const nextUserMe = {
    ...userMe,
    properties: {
      ...userMe.properties,
      favoriteDevices: nextFavoriteDevices,
    },
  };

  try {
    userMeDraftStore.setUserMeDraft({ userMe: nextUserMe });

    await api.users.updateUserMeProperties?.({
      id: 'favoriteDevices',
      value: nextFavoriteDevices,
    });

    toastAdd({
      icon: isFavoriteDevice === true ? glyph.core.heartOutline : glyph.core.heartFill,
      iconColor: isFavoriteDevice === true ? theme.color.icon_light : theme.color.special_favorite,
      message: i18n.messageFormatter(
        isFavoriteDevice === true ? 'device.removeFavorite' : 'device.addFavorite',
        { name: favoriteName },
      ),
    });
    userMeDraftStore.setUserMeDraftDone();
  } catch (error) {
    userMeDraftStore.setUserMeDraftFailed();
    ToastManager.handleError(error);
  }
}

function getDeviceQuickAction({
  device,
  api,
  i18n,
}: {
  device: DeviceProps;
  api: DeviceProviderApi;
  i18n: DeviceProviderI18n;
}): {
  title: string;
  capabilityId: string;
  nextValue: boolean;
  capabilityTitle: string | null;
} | null {
  if (api == null || device == null) {
    return null;
  }

  const requiresPremium =
    HomeyAPI.isHomeyAPIV3CloudItemProps(device) && device.requiresPremium === true;
  const isLocked = requiresPremium && api.tier !== 'premium';

  if (isLocked === true) {
    return null;
  }

  const quickActionOverride =
    typeof device.ui === 'object' && device.ui != null
      ? (device.ui as { quickActionOverride?: unknown }).quickActionOverride
      : null;
  let quickActionId =
    typeof device.ui === 'object' && device.ui != null
      ? (device.ui as { quickAction?: unknown }).quickAction
      : null;

  if (quickActionOverride === '.none') {
    return null;
  }

  if (
    typeof quickActionOverride === 'string' &&
    device.capabilitiesObj?.[quickActionOverride] != null
  ) {
    quickActionId = quickActionOverride;
  }

  if (typeof quickActionId !== 'string' || quickActionId === '') {
    return null;
  }

  const capabilityObj = device.capabilitiesObj?.[quickActionId] ?? null;

  if (capabilityObj == null || capabilityObj.setable !== true) {
    return null;
  }

  if (capabilityObj.getable === true && capabilityObj.type !== 'boolean') {
    return null;
  }

  const baseId = quickActionId?.split('.')[0];

  let title = i18n.messageFormatter('device.quickAction.default');

  if (baseId != null && SYSTEM_QUICK_ACTION_CAPABILITIES.includes(baseId)) {
    if (capabilityObj.getable === true) {
      title = i18n.messageFormatter(`device.quickAction.${baseId}.${capabilityObj.value ?? false}`);
    } else {
      title = i18n.messageFormatter(`device.quickAction.${baseId}`);
    }
  } else if (typeof capabilityObj.title === 'string' && capabilityObj.title !== '') {
    title = capabilityObj.title;
  }

  const nextValue = capabilityObj.getable === true ? capabilityObj.value !== true : true;

  return {
    title,
    capabilityId: quickActionId,
    nextValue,
    capabilityTitle: capabilityObj.title ?? null,
  };
}

export function getDeviceCommands(args: CommandProviderArgs): CommandItem[] {
  const {
    query,
    activeContext,
    devicesById,
    zonesById,
    flowsById,
    advancedFlowsById,
    logsById,
    api,
    i18n,
    userMe,
    appsById,
  } = args;

  const normalizedQuery = normalize(query.trim());
  const openTitle = i18n.messageFormatter('commandPalette.actions.open');
  const copyIdTitle = i18n.messageFormatter('commandPalette.actions.copyId');
  const copyJsonTitle = i18n.messageFormatter('commandPalette.actions.copyJson');
  const isDevicesScopeContext =
    activeContext?.type === 'scope' && activeContext.scopeType === 'devices';
  const scopeCommands: CommandItem[] = isDevicesScopeContext
    ? [
        {
          key: 'cmd:scope:devices:open',
          kind: 'action',
          section: COMMAND_SECTIONS.NAVIGATION,
          sortOrder: 10,
          title: openTitle,
          subtitle: i18n.messageFormatter('commandPalette.actions.devicesPage'),
          icon: glyph.core.squareArrowUpRight,
          keywords: ['open', 'devices', 'go to devices'],
          getOpenInNewTabPath() {
            return RouteManager.getPathForDevices();
          },
          onRun() {
            RouteManager.toDevices();
          },
        },
        {
          key: 'cmd:scope:devices:new-device',
          kind: 'action',
          section: COMMAND_SECTIONS.CONTEXT_ACTIONS,
          sortOrder: 20,
          title: i18n.messageFormatter('header.action.device'),
          subtitle: i18n.messageFormatter('commandPalette.actions.addNewDevice'),
          icon: glyph.system.devices,
          keywords: ['new device', 'add device', 'pair'],
          onRun() {
            RouteManager.toNewDevice();
          },
        },
      ]
    : [];

  if (activeContext?.type === 'device') {
    let device = null;

    if (activeContext.deviceId != null) {
      device = devicesById?.[activeContext.deviceId] ?? null;
    }

    if (device == null) {
      return [];
    }

    const quickAction = getDeviceQuickAction({ device, api, i18n });

    const isFavoriteDevice = DeviceUtils.isFavoriteDevice(device, userMe);
    const appId = getAppIdFromDriverId(device.driverId);
    const app = appId != null ? appsById?.[appId] : null;
    const numberDeviceLogIds = getNumberDeviceLogIds({ deviceId: device.id, logsById });
    const numberLogsCount = getNumberDeviceLogsCount({ deviceId: device?.id, logsById });
    const flowsCount = getDeviceFlowsCount({
      deviceId: device?.id,
      flowsById,
      advancedFlowsById,
    });
    const logsSubtitle =
      numberLogsCount == null
        ? device?.name
        : numberLogsCount === 1
          ? i18n.messageFormatter('commandPalette.count.logOne')
          : i18n.messageFormatter('commandPalette.count.logMany', { count: numberLogsCount });
    const flowsSubtitle =
      flowsCount == null
        ? device?.name
        : flowsCount === 1
          ? i18n.messageFormatter('commandPalette.count.flowOne')
          : i18n.messageFormatter('commandPalette.count.flowMany', { count: flowsCount });

    return [
      {
        key: `cmd:device:${device.id}:open`,
        kind: 'action',
        section: COMMAND_SECTIONS.CONTEXT_NAVIGATE,
        sortOrder: 10,
        title: openTitle,
        subtitle: device.name,
        icon: glyph.core.squareArrowUpRight,
        keywords: ['open', 'device page', 'go to device'],
        getOpenInNewTabPath() {
          return RouteManager.getPathForDevice(device.id);
        },
        onRun() {
          RouteManager.toDevice(device.id);
        },
      },
      {
        key: `cmd:device:${device.id}:settings`,
        kind: 'action',
        section: COMMAND_SECTIONS.CONTEXT_NAVIGATE,
        sortOrder: 20,
        title: i18n.messageFormatter('common.settings'),
        subtitle: device.name,
        icon: glyph.system.settings,
        keywords: ['device settings', 'settings', 'configure'],
        getOpenInNewTabPath() {
          return RouteManager.getPathForDeviceSettings(device.id);
        },
        onRun() {
          RouteManager.toDeviceSettings(device.id);
        },
      },
      {
        key: `cmd:device:${device.id}:logs`,
        kind: 'action',
        section: COMMAND_SECTIONS.CONTEXT_NAVIGATE,
        sortOrder: 22,
        title: i18n.messageFormatter('scopes.insights'),
        subtitle: logsSubtitle,
        icon: glyph.system.insights,
        keywords: ['logs', 'insights', 'device logs', 'device insights'],
        onRun() {
          RouteManager.toInsights({
            defaultSelectedKeys: numberDeviceLogIds,
            deviceId: device.id,
          });
        },
      },
      {
        key: `cmd:device:${device.id}:flows`,
        kind: 'action',
        section: COMMAND_SECTIONS.CONTEXT_NAVIGATE,
        sortOrder: 23,
        title: i18n.messageFormatter('scopes.flow'),
        subtitle: flowsSubtitle,
        icon: glyph.system.flow,
        keywords: ['flows', 'device flows', 'automation'],
        onRun() {
          RouteManager.toFlows({
            deviceId: device.id,
          });
        },
      },
      ...(app == null
        ? []
        : [
            {
              key: `cmd:device:${device.id}:open-app`,
              kind: 'action',
              section: COMMAND_SECTIONS.CONTEXT_NAVIGATE,
              sortOrder: 25,
              title: i18n.messageFormatter('commandPalette.actions.openApp'),
              subtitle: app.name ?? app.id,
              icon: glyph.core.squareArrowUpRight,
              keywords: ['open app', 'app settings', app.id, app.name, device.driverId].filter(
                (value): value is string => Boolean(value),
              ),
              getOpenInNewTabPath() {
                return RouteManager.getPathForSetting(`apps/${app.id}`);
              },
              onRun() {
                RouteManager.toSetting(`apps/${app.id}`);
              },
            },
          ]),
      ...(quickAction == null
        ? []
        : [
            {
              key: `cmd:device:${device.id}:quick-action`,
              kind: 'action',
              section: COMMAND_SECTIONS.CONTEXT_ACTIONS,
              sortOrder: 30,
              title: quickAction.title,
              subtitle: device.name,
              icon: glyph.core.play,
              keepOpenOnShiftEnter: true,
              keywords: [
                'quick action',
                'toggle',
                quickAction.capabilityId,
                quickAction.capabilityTitle,
              ].filter((value): value is string => Boolean(value)),
              async onRun() {
                await api?.devices?.setCapabilityValue?.({
                  deviceId: device.id,
                  capabilityId: quickAction.capabilityId,
                  value: quickAction.nextValue,
                });
              },
            },
          ]),
      ...(userMe == null
        ? []
        : [
            {
              key: `cmd:device:${device.id}:favorite`,
              kind: 'action',
              section: COMMAND_SECTIONS.CONTEXT_ACTIONS,
              sortOrder: 35,
              title: i18n.messageFormatter('device.favorite'),
              subtitle: device.name,
              icon: isFavoriteDevice === true ? glyph.core.heartFill : glyph.core.heartOutline,
              keepOpenOnShiftEnter: true,
              keywords: ['favorite', 'unfavorite', 'heart'],
              async onRun() {
                await toggleFavoriteDevice({
                  device,
                  userMe,
                  api,
                  i18n,
                });
              },
            },
          ]),
      {
        key: `cmd:device:${device.id}:copy-id`,
        kind: 'action',
        section: COMMAND_SECTIONS.CONTEXT_DEVELOPER,
        sortOrder: 40,
        title: copyIdTitle,
        subtitle: device.name,
        icon: glyph.core.squareOnSquare,
        keywords: ['copy', 'device id', 'id'],
        onRun() {
          return copyToClipboard({ value: device.id, icon: glyph.core.squareOnSquare });
        },
      },
      {
        key: `cmd:device:${device.id}:copy-json`,
        kind: 'action',
        section: COMMAND_SECTIONS.CONTEXT_DEVELOPER,
        sortOrder: 50,
        title: copyJsonTitle,
        subtitle: device.name,
        icon: glyph.core.squareOnSquare,
        keywords: ['copy', 'json', 'device json'],
        onRun() {
          return copyToClipboard({
            value: JSON.stringify(device, null, 2),
            icon: glyph.core.squareOnSquare,
          });
        },
      },
    ];
  }

  if (activeContext?.type === 'scope' && activeContext.scopeType === 'flows') {
    return [];
  }

  if (activeContext?.type === 'scope' && activeContext.scopeType === 'settings') {
    return [];
  }

  if (activeContext?.type === 'scope' && activeContext.scopeType === 'logs') {
    return [];
  }

  if (activeContext?.type === 'app') {
    return [];
  }

  if (normalizedQuery === '' && isDevicesScopeContext !== true) {
    return [];
  }

  const commands: CommandItem[] = [...scopeCommands];

  for (const device of Object.values(devicesById ?? {})) {
    const zoneName = zonesById?.[device.zone]?.name ?? '';

    if (
      isMatch(device.name, normalizedQuery) !== true &&
      isMatch(zoneName, normalizedQuery) !== true &&
      isMatch(device.driverId, normalizedQuery) !== true &&
      isMatch(device.id, normalizedQuery) !== true
    ) {
      continue;
    }

    commands.push({
      key: `entity:device:${device.id}`,
      kind: 'entity',
      section: COMMAND_SECTIONS.DEVICES,
      title: device.name ?? device.id,
      subtitle: zoneName ?? device.id,
      icon: glyph.system.devices,
      keywords: [device.id, device.driverId, zoneName].filter((value) => Boolean(value)),
      meta: {
        entityType: 'device',
        entityId: device.id,
        canEnterContextWithTab: true,
        deviceIconObj: device.iconObj ?? null,
        deviceIconOverride: device.iconOverride ?? null,
      },
      getOpenInNewTabPath() {
        return RouteManager.getPathForDevice(device.id);
      },
      onRun() {
        RouteManager.toDevice(device.id);
      },
    });
  }

  return commands;
}
